import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Upload, RefreshCw, FileText, Search, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import ClientPicker from "@/components/ClientPicker";
import IngestFileModal from "@/components/ingestfiles/IngestFileModal";
import IngestFileGroup from "@/components/ingestfiles/IngestFileGroup";
import { typeMeta } from "@/components/ingestfiles/FileTypeBadge";

/** Type filter — "All" plus one pill per type with its file count. */
function TypeFilter({ types, counts, value, onChange }) {
    const total = Object.values(counts || {}).reduce((a, b) => a + b, 0);
    const pill = (on) => `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] transition-colors ${on ? "bg-white text-black border-white font-medium" : "border-white/15 text-white/60 hover:border-white/30 hover:text-white"}`;
    return (
        <div className="flex flex-wrap gap-1.5" data-testid="ingest-files-type-filter">
            <button type="button" onClick={() => onChange("")} className={pill(!value)} data-testid="ingest-files-filter-all">All <span className="opacity-60">{total}</span></button>
            {types.map((t) => {
                const Icon = typeMeta(t.id).icon;
                return (
                    <button key={t.id} type="button" onClick={() => onChange(value === t.id ? "" : t.id)} className={pill(value === t.id)} data-testid={`ingest-files-filter-${t.id}`}>
                        <Icon className="w-3 h-3" strokeWidth={1.75} />{t.label} <span className="opacity-60">{counts?.[t.id] || 0}</span>
                    </button>
                );
            })}
        </div>
    );
}

export default function IngestFilesPage() {
    const [params, setParams] = useSearchParams();
    const [meta, setMeta] = useState(null);
    const [data, setData] = useState({ items: [], counts: {}, files: 0 });
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [q, setQ] = useState("");
    const clientId = params.get("client") || "";
    const fileType = params.get("type") || "";

    const setParam = (k, v) => {
        const next = new URLSearchParams(params);
        if (v) next.set(k, v); else next.delete(k);
        setParams(next, { replace: true });
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = new URLSearchParams();
            if (clientId) qs.set("client_id", clientId);
            if (fileType) qs.set("file_type", fileType);
            if (q.trim()) qs.set("q", q.trim());
            const { data: d } = await api.get(`/admin/ingest-files?${qs}`);
            setData(d);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setLoading(false); }
    }, [clientId, fileType, q]);

    useEffect(() => { api.get("/admin/ingest-files/_meta").then(({ data: d }) => setMeta(d)).catch(() => setMeta({ types: [] })); }, []);
    useEffect(() => { load(); }, [load]);

    const types = useMemo(() => meta?.types || [], [meta]);

    return (
        <div className="px-8 py-8 max-w-[1600px] mx-auto" data-testid="ingest-files-page">
            <div className="flex items-start justify-between gap-6 mb-7 flex-wrap">
                <div className="max-w-3xl">
                    <h1 className="text-2xl font-display tracking-tight mb-1">Ingest Files</h1>
                    <p className="text-sm text-white/50">
                        One ingestion for every study document: pick the type, the client and the file. Each file is stored, its text extracted, summarised
                        and added to the <Link to="/datalake" className="underline">Data Lake</Link>; re-ingesting for the same study creates a new version.
                        <strong className="text-white/70"> EDC Guidelines</strong> also get their form families extracted for <Link to="/qa/form-generation" className="underline">eSource form generation</Link>.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={load} data-testid="ingest-files-refresh" className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-xs text-white/70 hover:text-white hover:bg-white/5">
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />Refresh
                    </button>
                    <button type="button" onClick={() => setModal({ type: fileType || "" })} data-testid="ingest-files-ingest-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-white text-black text-sm font-medium hover:bg-gray-200">
                        <Upload className="w-3.5 h-3.5" strokeWidth={1.75} />Ingest file
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap mb-4">
                <TypeFilter types={types} counts={data.counts} value={fileType} onChange={(v) => setParam("type", v)} />
                <div className="ml-auto flex items-center gap-2">
                    <ClientPicker compact includeSuperdomain anyOptionLabel="(every client)" value={clientId} onChange={(v) => setParam("client", v)} testId="ingest-files-client-filter" />
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/35" strokeWidth={1.5} />
                        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="title, study, protocol, topic…" data-testid="ingest-files-search"
                            className="bg-white/5 border border-white/10 rounded-md pl-7 pr-2 py-1 text-xs w-56 focus:outline-none focus:ring-2 focus:ring-white/30" />
                    </div>
                </div>
            </div>

            {loading && !data.items.length ? (
                <div className="text-white/40 text-sm" data-testid="ingest-files-loading">Loading…</div>
            ) : data.items.length === 0 ? (
                <div className="p-10 rounded-md border border-dashed border-white/15 text-center" data-testid="ingest-files-empty">
                    <FileText className="w-8 h-8 mx-auto text-white/20 mb-3" strokeWidth={1.5} />
                    <div className="text-sm text-white/60 mb-1">{fileType || clientId || q ? "No files match these filters" : "No files ingested yet"}</div>
                    <div className="text-xs text-white/40 mb-4">EDC Guidelines, Manuals, Investigator Brochures, Newsletters, Training material — any study document.</div>
                    <button type="button" onClick={() => setModal({ type: fileType || "" })} data-testid="ingest-files-empty-ingest" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-black text-sm font-medium hover:bg-gray-200">
                        <Upload className="w-3.5 h-3.5" strokeWidth={1.75} />Ingest file
                    </button>
                </div>
            ) : (
                <div className="space-y-3" data-testid="ingest-files-list">
                    <div className="text-[11px] text-white/40 flex items-center gap-1.5" data-testid="ingest-files-summary"><Layers className="w-3 h-3" strokeWidth={1.5} />{data.total} chain{data.total === 1 ? "" : "s"} · {data.files} file version{data.files === 1 ? "" : "s"}</div>
                    {data.items.map((g) => {
                        const key = `${g.company_id}::${g.identity}::${g.file_type}`;
                        return <IngestFileGroup key={key} group={g} expanded={expanded === key} onToggle={() => setExpanded(expanded === key ? null : key)} onChanged={load} />;
                    })}
                </div>
            )}

            {modal && <IngestFileModal meta={meta} defaultType={modal.type} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
        </div>
    );
}
